/**
 * Signup — "Get Started" from the marketing nav. Email-first, matching
 * Slack's own flow: verify identity BEFORE ever asking for a password, not
 * after (see auth.service.ts#startSignup/#verifySignupOtp/#completeSignup on
 * the backend for why this is a 3-step exchange rather than one form).
 *
 * Steps live as component state, not separate routes/URL params — the
 * signup ticket from step 2 is security-sensitive proof of email ownership
 * and must never end up in the URL/browser history/referrer logs.
 *
 * Google sign-in short-circuits the whole thing from step 1: Google's own
 * verification replaces both the OTP step and the password (a Google-only
 * account has none) — see useAuth.ts#googlePreview. A known Google identity
 * logs straight in; a brand-new one is handed to /auth/google/confirm to be
 * reviewed before the account is created, never used unconfirmed.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, User, ShieldCheck } from 'lucide-react';
import { ThemeCtx, useTheme, useThemeState, THEMES, NavBar, Footer } from './home/marketing-shell';
import { useAuth } from '@/hooks/useAuth';
import type { DomainClaimWorkspace } from '@/services/auth.api';
import { isFreeEmailDomain } from '@/utils/email-domain.util';
import GoogleSignInButton, { isGoogleSignInConfigured } from '@/components/auth/GoogleSignInButton';
import TurnstileWidget, { isTurnstileConfigured } from '@/components/auth/TurnstileWidget';
import PasswordField from '@/components/auth/PasswordField';

type Step = 'email' | 'otp' | 'details';

const RESEND_COOLDOWN_MS = 60_000; // matches the backend's emailCooldown middleware

function useSharedFieldStyles() {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px 11px 40px',
    borderRadius: 10,
    background: t.muted,
    border: `1px solid ${t.border}`,
    color: t.fg,
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    outline: 'none',
  };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 600, color: t.mutedFg, marginBottom: 6 };
  const errorStyle: React.CSSProperties = { fontSize: 11.5, color: '#ef4444', marginTop: 4 };
  const iconStyle: React.CSSProperties = { position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' };
  const focusHandlers = {
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => { e.currentTarget.style.borderColor = t.green; },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => { e.currentTarget.style.borderColor = t.border; },
  };
  return { t, inputStyle, labelStyle, errorStyle, iconStyle, focusHandlers };
}

function WizardCard({ children }: { children: React.ReactNode }) {
  const { t } = useSharedFieldStyles();
  return (
    <div style={{
      background: t.card, border: `1px solid ${t.border}`, borderRadius: 24, padding: '36px 32px',
      display: 'flex', flexDirection: 'column', gap: 18, width: '100%', maxWidth: 440,
    }}>
      {children}
    </div>
  );
}

function PrimaryButton({
  children,
  disabled,
  type = 'submit',
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  type?: 'submit' | 'button';
  onClick?: () => void;
}) {
  const { theme } = useTheme();
  const t = THEMES[theme];
  const isDark = theme === 'dark';
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '14px 24px', borderRadius: 12, border: 'none', cursor: disabled ? 'default' : 'pointer',
        background: t.green, color: isDark ? '#050C09' : '#fff', fontWeight: 800, fontSize: 15,
        fontFamily: 'Manrope, sans-serif', opacity: disabled ? 0.7 : 1,
        boxShadow: `0 8px 30px ${isDark ? 'rgba(61,220,132,0.35)' : 'rgba(37,211,102,0.4)'}`,
      }}
    >
      {children}
    </button>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{ color: '#ef4444', fontSize: 13, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 14px' }}>
      {message}
    </div>
  );
}

/** Step 1: email + Google, matching Slack's "First, enter your email" screen. */
function EmailStep({ onContinue, onGoogleSuccess }: { onContinue: (email: string) => void; onGoogleSuccess: (idToken: string) => void }) {
  const { t, inputStyle, labelStyle, errorStyle, iconStyle, focusHandlers } = useSharedFieldStyles();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const { startSignup, checkDomain, requestToJoin, loading } = useAuth();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  // Advisory only, matching Slack's own "using your work email will make it
  // easier for coworkers to join you" banner — never blocks submission, a
  // free-provider signup is fully allowed (see email-domain.util.ts).
  const [dismissedWorkEmailHint, setDismissedWorkEmailHint] = useState(false);
  const showWorkEmailHint =
    !dismissedWorkEmailHint && email.includes('@') && isFreeEmailDomain(email.trim());
  // Honeypot — a real user never sees or fills this (visually hidden, not
  // type="hidden", which some scrapers know to skip — see the input below).
  // A non-empty value means a script filled every field it could find.
  const [website, setWebsite] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  // Slack-style "workspace already exists" interstitial — checked on blur
  // for early feedback AND (authoritatively, awaited) on submit, before any
  // OTP is ever sent — see checkDomainAndMaybeShowInterstitial's doc comment
  // for why blur alone wasn't enough. NEVER blocks "create anyway": choosing
  // it sends the OTP immediately (see handleCreateAnyway) using the email
  // already entered and validated, exactly as if no domain match existed —
  // no second "Continue" click, and no flash back to the email form.
  //
  // A domain can map to MORE than one workspace (ownerEmailDomain is
  // deliberately non-unique — see Tenant.model.ts) — this shows every one
  // of them, each with its own independent "Request to join" action, rather
  // than picking an arbitrary single one to display.
  const [domainMatches, setDomainMatches] = useState<DomainClaimWorkspace[] | null>(null);
  // Per-tenantId state, since each workspace's join-request is independent —
  // requesting one must not disable or hide the others.
  const [joinRequestSent, setJoinRequestSent] = useState<Record<string, boolean>>({});
  const [joinRequestError, setJoinRequestError] = useState<Record<string, string>>({});
  const [joinRequestLoading, setJoinRequestLoading] = useState<Record<string, boolean>>({});
  // Set when the user explicitly chooses "Create a new workspace anyway".
  // Without this, dismissing the interstitial falls back to the same email
  // input still holding the same claimed address — the next blur event
  // (which fires again as soon as the re-rendered input mounts/loses focus)
  // would immediately re-run checkDomain, find the same match, and show the
  // interstitial right back. Cleared the moment the email actually changes,
  // so a genuinely different address still gets checked normally.
  const [bypassedDomainCheck, setBypassedDomainCheck] = useState(false);

  /** Returns true if the interstitial was shown (caller should stop). Shared
   *  by blur (so the interstitial can appear as early as possible, before
   *  the user even reaches for Continue) AND submit (the authoritative
   *  check): blur alone raced with submit — pressing Enter, or clicking
   *  Continue right after typing, could fire handleSubmit's startSignup
   *  before this unawaited blur-triggered check had come back, so whichever
   *  of the two network calls happened to resolve first decided whether the
   *  interstitial ever appeared. Awaiting this directly inside handleSubmit
   *  removes that race — the check is now on the same call stack as
   *  submission, not a fire-and-forget side effect of a DOM event that may
   *  or may not even fire before submit does. */
  async function checkDomainAndMaybeShowInterstitial(trimmed: string): Promise<boolean> {
    if (bypassedDomainCheck) return false;
    if (!trimmed || isFreeEmailDomain(trimmed) || !/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(trimmed)) return false;
    const result = await checkDomain(trimmed);
    if (result.success && result.result.claimed) {
      setDomainMatches(result.result.workspaces);
      return true;
    }
    return false;
  }

  async function handleRequestToJoin(tenantId: string) {
    setJoinRequestLoading((prev) => ({ ...prev, [tenantId]: true }));
    setJoinRequestError((prev) => ({ ...prev, [tenantId]: '' }));
    const result = await requestToJoin(email.trim(), tenantId);
    setJoinRequestLoading((prev) => ({ ...prev, [tenantId]: false }));
    if (result.success) {
      setJoinRequestSent((prev) => ({ ...prev, [tenantId]: true }));
    } else {
      setJoinRequestError((prev) => ({ ...prev, [tenantId]: result.error || 'Could not send the request' }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const trimmed = email.trim();
    if (!/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(trimmed)) {
      setError('Enter a valid email address');
      return;
    }
    // Authoritative check, awaited — see this function's doc comment on why
    // relying on blur alone raced with submit. If the interstitial appears,
    // stop here; startSignup only ever runs once this has cleared.
    if (await checkDomainAndMaybeShowInterstitial(trimmed)) return;

    const result = await startSignup(trimmed, { turnstileToken, website });
    if (result.success) {
      onContinue(trimmed);
    } else {
      setError(result.error || 'Something went wrong. Please try again.');
    }
  }

  async function handleCreateAnyway() {
    const trimmed = email.trim();
    setBypassedDomainCheck(true);
    setError('');

    // Deliberately does NOT clear domainMatches before awaiting: doing so
    // re-renders straight back to the plain email form for the duration of
    // the network call, which flashes the signup screen the user just chose
    // to move past. Stay on the interstitial (its button shows "Sending
    // code...") until the OTP is actually sent, then go straight to the OTP
    // step. Only a FAILED send drops back to the form, where the error
    // belongs and can be acted on.
    const result = await startSignup(trimmed, { turnstileToken, website });
    if (result.success) {
      onContinue(trimmed);
    } else {
      setDomainMatches(null);
      setError(result.error || 'Something went wrong. Please try again.');
    }
  }

  if (domainMatches) {
    return (
      <WizardCard>
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 24, color: t.fg, marginBottom: 6 }}>
            {domainMatches.length === 1 && domainMatches[0]
              ? `${domainMatches[0].organizationName} is already on Baatly`
              : `${domainMatches.length} workspaces at your company are already on Baatly`}
          </h1>
          <p style={{ color: t.mutedFg, fontSize: 14.5 }}>
            {domainMatches.length === 1
              ? 'Someone at your company already set up a workspace. You can ask their admin to add you, or start a brand new workspace of your own.'
              : "Ask an admin to add you to one of them, or start a brand new workspace of your own."}
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {domainMatches.map((workspace) => (
            <div
              key={workspace.tenantId}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                background: t.muted, border: `1px solid ${t.border}`, borderRadius: 10, padding: '12px 14px',
              }}
            >
              <span style={{ fontSize: 14.5, fontWeight: 700, color: t.fg }}>{workspace.organizationName}</span>
              {joinRequestSent[workspace.tenantId] ? (
                <span style={{ fontSize: 12.5, color: t.mutedFg }}>Request sent</span>
              ) : (
                <button
                  type="button"
                  disabled={joinRequestLoading[workspace.tenantId]}
                  onClick={() => handleRequestToJoin(workspace.tenantId)}
                  style={{
                    background: t.green, border: 'none', borderRadius: 8, padding: '8px 14px',
                    fontSize: 13, fontWeight: 700, fontFamily: 'Manrope, sans-serif',
                    color: isDark ? '#050C09' : '#fff',
                    cursor: joinRequestLoading[workspace.tenantId] ? 'default' : 'pointer',
                    opacity: joinRequestLoading[workspace.tenantId] ? 0.7 : 1, flexShrink: 0,
                  }}
                >
                  {joinRequestLoading[workspace.tenantId] ? 'Sending...' : 'Request to join'}
                </button>
              )}
            </div>
          ))}
        </div>
        {Object.values(joinRequestError).some(Boolean) && (
          <ErrorBanner message={Object.values(joinRequestError).find(Boolean) || 'Could not send the request'} />
        )}

        <button
          type="button"
          onClick={handleCreateAnyway}
          disabled={loading}
          style={{
            background: 'none', border: 'none', cursor: loading ? 'default' : 'pointer', color: t.mutedFg,
            fontSize: 13.5, fontWeight: 600, padding: '4px 0', textAlign: 'center', opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Sending code...' : 'Create a new workspace anyway →'}
        </button>
      </WizardCard>
    );
  }

  return (
    <WizardCard>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 26, color: t.fg, marginBottom: 6 }}>
            First, enter your email
          </h1>
          <p style={{ color: t.mutedFg, fontSize: 14.5 }}>We'll send a code to verify it's really you.</p>
        </div>

        <div>
          <label style={labelStyle}>Work email</label>
          <div style={{ position: 'relative' }}>
            <Mail size={15} color={t.mutedFg} style={iconStyle} />
            <input
              style={inputStyle}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setDismissedWorkEmailHint(false); setBypassedDomainCheck(false); }}
              placeholder="you@company.com"
              autoComplete="email"
              autoFocus
              {...focusHandlers}
              onBlur={(e) => { focusHandlers.onBlur(e); checkDomainAndMaybeShowInterstitial(email.trim()); }}
            />
          </div>
          {showWorkEmailHint && !error && (
            <div style={{
              marginTop: 8, fontSize: 12.5, lineHeight: 1.5, color: t.mutedFg,
              background: t.muted, border: `1px solid ${t.border}`, borderRadius: 8, padding: '9px 12px',
              display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <span style={{ flex: 1 }}>
                Using your <strong style={{ color: t.fg }}>work email</strong> will make it easier for
                coworkers to join your organization later.
              </span>
              <button
                type="button"
                onClick={() => setDismissedWorkEmailHint(true)}
                aria-label="Dismiss"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: t.mutedFg,
                  fontSize: 15, lineHeight: 1, padding: 0, flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
          )}
          {error && <div style={errorStyle}>{error}</div>}
        </div>

        {/* Honeypot — off-screen (not display:none/type="hidden", which some
            scrapers already know to skip), never focusable by a real user
            tabbing through the form (tabIndex -1, aria-hidden). */}
        <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
          <label htmlFor="website">Leave this field empty</label>
          <input
            id="website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        {isTurnstileConfigured && (
          <TurnstileWidget onToken={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
        )}

        <PrimaryButton disabled={loading}>
          {loading ? 'Sending code...' : 'Continue'} {!loading && <ArrowRight size={16} />}
        </PrimaryButton>

        {isGoogleSignInConfigured && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, height: 1, background: t.divider }} />
              <span style={{ fontSize: 12, color: t.mutedFg, fontWeight: 600 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: t.divider }} />
            </div>
            <GoogleSignInButton onSuccess={onGoogleSuccess} width={376} />
          </>
        )}

        <p style={{ textAlign: 'center', fontSize: 13.5, color: t.mutedFg }}>
          Already have an account?{' '}
          <a
            href="/login"
            onClick={(e) => { e.preventDefault(); navigate('/login'); }}
            style={{ color: t.green, fontWeight: 700, textDecoration: 'none', cursor: 'pointer' }}
          >
            Sign in
          </a>
        </p>
      </form>
    </WizardCard>
  );
}

/** Step 2: enter the 6-digit code. */
function OtpStep({
  email,
  onVerified,
  onUseDifferentEmail,
}: {
  email: string;
  onVerified: (signupTicket: string) => void;
  onUseDifferentEmail: () => void;
}) {
  const { t, labelStyle, errorStyle, focusHandlers } = useSharedFieldStyles();
  const { startSignup, verifySignupOtp, loading } = useAuth();

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [cooldownUntil, setCooldownUntil] = useState(() => Date.now() + RESEND_COOLDOWN_MS);
  const [now, setNow] = useState(() => Date.now());
  const secondsLeft = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [secondsLeft > 0]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(otp)) {
      setError('Enter the 6-digit code');
      return;
    }
    const result = await verifySignupOtp(email, otp);
    if (result.success && result.signupTicket) {
      onVerified(result.signupTicket);
    } else {
      setError(result.error || 'Invalid or expired code');
    }
  }

  async function handleResend() {
    if (secondsLeft > 0) return;
    setResendState('sending');
    setError('');
    const result = await startSignup(email);
    if (result.success) {
      setResendState('sent');
      setCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);
    } else {
      setResendState('idle');
      setError(result.error || 'Could not resend the code');
    }
  }

  return (
    <WizardCard>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: t.greenDim, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <ShieldCheck size={24} color={t.green} />
          </div>
          <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 24, color: t.fg, marginBottom: 6 }}>
            Enter your code
          </h1>
          <p style={{ color: t.mutedFg, fontSize: 14 }}>
            We sent a 6-digit code to <strong style={{ color: t.fg }}>{email}</strong>
          </p>
        </div>

        <div>
          <label style={labelStyle}>Verification code</label>
          <input
            style={{
              width: '100%', padding: '14px', borderRadius: 10, background: t.muted,
              border: `1px solid ${t.border}`, color: t.fg, fontSize: 22, fontWeight: 700,
              letterSpacing: '0.5em', textAlign: 'center', fontFamily: "'Manrope', sans-serif", outline: 'none',
            }}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            maxLength={6}
            placeholder="000000"
            autoFocus
            {...focusHandlers}
          />
          {error && <div style={errorStyle}>{error}</div>}
        </div>

        <PrimaryButton disabled={loading}>
          {loading ? 'Verifying...' : 'Verify'} {!loading && <ArrowRight size={16} />}
        </PrimaryButton>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <button
            type="button"
            onClick={onUseDifferentEmail}
            style={{ background: 'none', border: 'none', color: t.mutedFg, cursor: 'pointer', padding: 0, fontSize: 13 }}
          >
            Use a different email
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendState === 'sending' || secondsLeft > 0}
            style={{
              background: 'none', border: 'none', color: t.green, fontWeight: 600, padding: 0, fontSize: 13,
              cursor: secondsLeft > 0 ? 'default' : 'pointer', opacity: secondsLeft > 0 ? 0.6 : 1,
            }}
          >
            {resendState === 'sending' ? 'Sending...' : secondsLeft > 0 ? `Resend in ${secondsLeft}s` : 'Resend code'}
          </button>
        </div>
      </form>
    </WizardCard>
  );
}

/** Step 3: name + password — the identity is already verified at this point. */
function DetailsStep({ signupTicket, onExpired }: { signupTicket: string; onExpired: () => void }) {
  const { t, inputStyle, labelStyle, errorStyle, iconStyle, focusHandlers } = useSharedFieldStyles();
  const { completeSignup, loading } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<'firstName' | 'lastName' | 'password' | 'confirmPassword', string>>>({});
  const [submitError, setSubmitError] = useState('');

  function validate(): boolean {
    const next: typeof errors = {};
    if (!firstName.trim()) next.firstName = 'Required';
    if (!lastName.trim()) next.lastName = 'Required';
    if (password.length < 8) next.password = 'At least 8 characters';
    if (confirmPassword !== password) next.confirmPassword = 'Passwords do not match';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    const result = await completeSignup({
      signupTicket,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      password,
    });
    if (!result.success) {
      const message = result.error || 'Something went wrong. Please try again.';
      setSubmitError(message);
      // A 401 here specifically means the ticket expired/was invalid —
      // nothing on this step can recover from that, only starting over can.
      if (/expired|invalid/i.test(message)) {
        setTimeout(onExpired, 2500);
      }
    }
  }

  return (
    <WizardCard>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <h1 style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 24, color: t.fg, marginBottom: 6 }}>
            Almost there
          </h1>
          <p style={{ color: t.mutedFg, fontSize: 14.5 }}>Your email is verified — now set up your account.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="signup-grid-2">
          <div>
            <label style={labelStyle}>First name</label>
            <div style={{ position: 'relative' }}>
              <User size={15} color={t.mutedFg} style={iconStyle} />
              <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" autoFocus {...focusHandlers} />
            </div>
            {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
          </div>
          <div>
            <label style={labelStyle}>Last name</label>
            <div style={{ position: 'relative' }}>
              <User size={15} color={t.mutedFg} style={iconStyle} />
              <input style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" {...focusHandlers} />
            </div>
            {errors.lastName && <div style={errorStyle}>{errors.lastName}</div>}
          </div>
        </div>

        <div>
          <label style={labelStyle}>Password</label>
          <PasswordField
            value={password}
            onChange={setPassword}
            visible={passwordVisible}
            onToggleVisible={() => setPasswordVisible((v) => !v)}
            placeholder="At least 8 characters"
          />
          {errors.password && <div style={errorStyle}>{errors.password}</div>}
        </div>

        <div>
          <label style={labelStyle}>Confirm password</label>
          <PasswordField
            value={confirmPassword}
            onChange={setConfirmPassword}
            visible={passwordVisible}
            showToggle={false}
            placeholder="Re-enter your password"
            autoComplete="new-password"
          />
          {errors.confirmPassword && <div style={errorStyle}>{errors.confirmPassword}</div>}
        </div>

        {submitError && <ErrorBanner message={submitError} />}

        <PrimaryButton disabled={loading}>
          {loading ? 'Creating account...' : 'Create free account'} {!loading && <ArrowRight size={16} />}
        </PrimaryButton>
        <style>{`.signup-grid-2 { @media (max-width: 420px) { grid-template-columns: 1fr !important; } }`}</style>
      </form>
    </WizardCard>
  );
}

function SignupWizard() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { googlePreview } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [signupTicket, setSignupTicket] = useState('');
  const [googleError, setGoogleError] = useState('');

  async function handleGoogleSuccess(idToken: string) {
    setGoogleError('');
    const result = await googlePreview(idToken);
    if (!result.success) {
      setGoogleError(result.error || 'Google sign-in failed');
      return;
    }
    // A known identity is already logged in at this point (googlePreview
    // handled it). A brand-new one still needs the user to confirm the data
    // before any account exists — hand off to that screen instead.
    if (result.isNewIdentity) {
      navigate('/auth/google/confirm', {
        state: { googleTicket: result.googleTicket, preview: result.preview },
      });
    }
  }

  return (
    <section style={{
      position: 'relative', paddingTop: 130, paddingBottom: 80, minHeight: '100vh',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: isDark
          ? 'radial-gradient(ellipse 120% 60% at 50% 0%, rgba(61,220,132,0.08) 0%, transparent 60%), #0D1117'
          : 'radial-gradient(ellipse 120% 60% at 50% 0%, rgba(37,211,102,0.10) 0%, transparent 60%), #F0FFF6',
        pointerEvents: 'none',
      }} />
      <div style={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '0 24px' }}>
        {googleError && <div style={{ width: '100%', maxWidth: 440 }}><ErrorBanner message={googleError} /></div>}
        {step === 'email' && (
          <EmailStep
            onContinue={(submittedEmail) => { setEmail(submittedEmail); setStep('otp'); }}
            onGoogleSuccess={handleGoogleSuccess}
          />
        )}
        {step === 'otp' && (
          <OtpStep
            email={email}
            onVerified={(ticket) => { setSignupTicket(ticket); setStep('details'); }}
            onUseDifferentEmail={() => { setEmail(''); setStep('email'); }}
          />
        )}
        {step === 'details' && (
          <DetailsStep signupTicket={signupTicket} onExpired={() => { setEmail(''); setSignupTicket(''); setStep('email'); }} />
        )}
      </div>
    </section>
  );
}

export default function Signup() {
  const themeCtxValue = useThemeState('light');
  const t = THEMES[themeCtxValue.theme];

  return (
    <ThemeCtx.Provider value={themeCtxValue}>
      <div style={{ minHeight: '100vh', background: t.bg, color: t.fg, fontFamily: "'Inter', sans-serif" }}>
        <NavBar />
        <main>
          <SignupWizard />
        </main>
        <Footer />
      </div>
    </ThemeCtx.Provider>
  );
}
